import { FC, useEffect, useState, useMemo } from 'react';
import { AcademicsViewProps } from './academics.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import {
  useAcademicsTabMenuItems,
  useAcademicsAvailableRouteIds,
} from './academics.helpers';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';
import { AcademicsFiltersProvider } from './contexts/academicsFilters.context';
import AcademicsFilterBar from './components/AcademicsFilterBar.component';
import { useRbacState } from 'src/context/rbac.context';
import { hasAnySubmoduleReadPermission } from 'src/utils/rbacHelpers';

const AcademicsView: FC<AcademicsViewProps> = () => {
  const { translateText } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { academicSubRouteId } = useParams();
  const navigate = useNavigate();
  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();
  const availableRouteIds = useAcademicsAvailableRouteIds();
  const tabs = useAcademicsTabMenuItems(translateText);

  const hasAccess = useMemo(
    () => hasAnySubmoduleReadPermission(rbac, 'academics'),
    [rbac]
  );

  useEffect(() => {
    if (availableRouteIds.length === 0) return;

    const routeId =
      academicSubRouteId && academicSubRouteId !== 'undefined'
        ? academicSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.academics.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const tabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(tabIndex >= 0 ? tabIndex : 0);

    if (!academicSubRouteId || academicSubRouteId === 'undefined') {
      navigate(pages.academics.path + '/' + routeId, { replace: true });
    }
  }, [academicSubRouteId, navigate, availableRouteIds]);

  if (!hasAccess) {
    return <Navigate to={safeLanding} replace />;
  }

  if (availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      navigate(pages.academics.path + '/' + availableRouteIds[tabIndex], {
        replace: true,
      });
    }
  };

  return (
    <NavigationDrawer
      selectedPage={pages.academics.path}
      window={window ?? null}
    >
      <Helmet>
        <title>{CONSTANTS.title.academics}</title>
      </Helmet>
      <AcademicsFiltersProvider>
        <Grid
          container
          sx={{ height: '100%', px: { xs: 0, sm: 0 } }}
          data-testid="academics-view-container"
        >
          <Grid
            item
            xs={12}
            sx={{ pr: { xs: 0, sm: 4 } }}
            data-testid="academics-view-header-container"
          >
            <MainTitle
              title={translateText('academics.title')}
              subTitle={selectedTab ? tabs[selectedTab].label : ''}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ height: '100%', px: { xs: 0, sm: 0 } }}
            data-testid="academics-view-tabs-container"
          >
            <TabGroup
              key={selectedTab}
              tabs={tabs}
              initiallySelected={selectedTab}
              onTabChange={(value) => locationChangeHandler(value)}
              filterSection={<AcademicsFilterBar />}
              dataTestIdPrefix="academics"
            />
          </Grid>
        </Grid>
      </AcademicsFiltersProvider>
    </NavigationDrawer>
  );
};

export default AcademicsView;
